/**
 * MerchantComposer — reply input for the storefront chat thread.
 *
 * Behaviours:
 *   - Auto-grow textarea
 *   - Enter sends, Shift+Enter newlines
 *   - Attach menu: Image / Products / Coupon / Button Link
 *       - Image  → POST /media/upload, attaches { type: 'image', url, name }
 *       - Products → search /merchants/products, attaches { type: 'product_card', ... }
 *       - Coupon → list /discounts?active=true, attaches { type: 'discount_code', code, label }
 *       - Button Link → ad-hoc { label, url }, attaches { type: 'button_link', label, url }
 *   - @ trigger: typing "@" opens an inline product picker; selection attaches
 *     a product card and removes the "@query" from the textarea.
 *   - Selected attachments render as inline chips above the textarea.
 *
 * Send disabled when conversation is CLOSED or while inflight. AI_HANDLING is
 * fine — backend auto-flips to MERCHANT_HANDLING on the first merchant reply.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Send, Paperclip, Image as ImageIcon, Package, Tag, Link2, X, Search,
} from 'lucide-react';
import axiosInstance from '../../axiosConfig';

// Discount model uses `type` (enum PERCENTAGE | FIXED_AMOUNT | FREE_SHIPPING
// | BOGO) and `value`. Accept legacy shapes too so this doesn't break if the
// API later renames fields.
function formatDiscountLabel(d = {}) {
  const type = d.type || d.discountType || 'FIXED_AMOUNT';
  const val = d.value ?? d.discountValue;
  if (type === 'FREE_SHIPPING') return 'Free shipping';
  if (type === 'BOGO') return 'Buy one, get one';
  if (val === null || val === undefined || val === '') return d.name || d.code || 'Discount';
  if (type === 'PERCENTAGE' || type === 'PERCENT') return `${val}% off`;
  return `${val} off`;
}

// ---------- Subcomponents -------------------------------------------------

const AttachChip = ({ attachment, onRemove }) => {
  let label = attachment.type;
  let detail = '';
  if (attachment.type === 'product_card') { label = 'Product'; detail = attachment.name; }
  else if (attachment.type === 'image')          { label = 'Image'; detail = attachment.name || 'attached'; }
  else if (attachment.type === 'discount_code')  { label = 'Coupon'; detail = attachment.code; }
  else if (attachment.type === 'button_link')    { label = 'Button'; detail = attachment.label; }
  return (
    <span className="mc-chip" title={detail}>
      <strong className="mc-chip__label">{label}</strong>
      {detail && <span className="mc-chip__detail">{detail}</span>}
      <button type="button" className="mc-chip__remove" onClick={onRemove} aria-label="Remove attachment">
        <X size={11} />
      </button>
    </span>
  );
};

const ProductPicker = ({ onPick, onClose }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get('/merchants/products', { params: { limit: 12, search: q || undefined } });
        if (cancelled) return;
        setItems(res?.data?.products || res?.data?.data || res?.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="mc-popover" role="dialog" aria-label="Pick product">
      <div className="mc-popover__head">
        <Search size={13} className="mc-popover__head-icon" aria-hidden />
        <input
          className="mc-popover__input"
          placeholder="Search products…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <button type="button" className="mc-popover__close" onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>
      <div className="mc-popover__body">
        {loading && items.length === 0 ? (
          <p className="mc-popover__empty">Searching…</p>
        ) : items.length === 0 ? (
          <p className="mc-popover__empty">No products match “{q}”.</p>
        ) : (
          items.slice(0, 12).map((p) => (
            <button
              key={p.id}
              type="button"
              className="mc-popover__item"
              onClick={() => onPick(p)}
            >
              {(p.primaryImage || (Array.isArray(p.gallery) && p.gallery[0])) && (
                <img src={p.primaryImage || p.gallery[0]} alt="" className="mc-popover__item-img" />
              )}
              <span className="mc-popover__item-body">
                <span className="mc-popover__item-name">{p.name}</span>
                <span className="mc-popover__item-meta">{p.sku || p.id.slice(0, 8)}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

const CouponPicker = ({ onPick, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/discounts', { params: { active: 'true', limit: 30 } });
        if (cancelled) return;
        setItems(res?.data?.discounts || res?.data?.data || res?.data || []);
      } catch (e) {
        if (!cancelled) setErr('Coupons need the marketing.discounts feature.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mc-popover" role="dialog" aria-label="Pick coupon">
      <div className="mc-popover__head">
        <Tag size={13} className="mc-popover__head-icon" aria-hidden />
        <span className="mc-popover__input" style={{ paddingLeft: 0, fontWeight: 600 }}>Active coupons</span>
        <button type="button" className="mc-popover__close" onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>
      <div className="mc-popover__body">
        {loading && <p className="mc-popover__empty">Loading…</p>}
        {!loading && err && <p className="mc-popover__empty">{err}</p>}
        {!loading && !err && items.length === 0 && <p className="mc-popover__empty">No active coupons right now.</p>}
        {items.map((d) => (
          <button
            key={d.id}
            type="button"
            className="mc-popover__item"
            onClick={() => onPick(d)}
          >
            <span className="mc-popover__item-body">
              <span className="mc-popover__item-name" style={{ fontFamily: 'ui-monospace, monospace' }}>{d.code}</span>
              <span className="mc-popover__item-meta">
                {formatDiscountLabel(d)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const ButtonLinkDialog = ({ onSave, onClose }) => {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const clean = url.trim();
    if (!label.trim() || !clean) return;
    onSave({ label: label.trim().slice(0, 40), url: clean });
  };
  return (
    <div className="mc-popover" role="dialog" aria-label="Add button link">
      <div className="mc-popover__head">
        <Link2 size={13} className="mc-popover__head-icon" aria-hidden />
        <span className="mc-popover__input" style={{ paddingLeft: 0, fontWeight: 600 }}>Add button link</span>
        <button type="button" className="mc-popover__close" onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>
      <form className="mc-popover__form" onSubmit={submit}>
        <input
          type="text"
          className="mc-popover__field"
          placeholder="Button label (e.g. View details)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={40}
          autoFocus
        />
        <input
          type="url"
          className="mc-popover__field"
          placeholder="https://…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
        />
        <button type="submit" className="mc-popover__submit" disabled={!label.trim() || !url.trim()}>
          Attach button
        </button>
      </form>
    </div>
  );
};

// ---------- Composer ------------------------------------------------------

const MerchantComposer = ({ conversationId, conversationStatus, onSent }) => {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null); // 'product' | 'coupon' | 'button' | 'mention' | null
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const isClosed = conversationStatus === 'CLOSED';

  // --- Auto-grow textarea --------------------------------------------------
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  // --- Send ---------------------------------------------------------------
  const send = useCallback(async () => {
    const trimmed = text.trim();
    if (sending || isClosed) return;
    if (!trimmed && attachments.length === 0) return;
    setSending(true);
    setError(null);
    try {
      await axiosInstance.post(`/merchant/chatbot/conversations/${conversationId}/reply`, {
        text: trimmed || (attachments.length ? '' : ' '),
        attachments: attachments.length ? attachments : undefined,
      });
      setText('');
      setAttachments([]);
      if (onSent) onSent();
    } catch (err) {
      console.error('[MerchantComposer] send failed:', err?.response?.data?.error || err?.message);
      setError(err?.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [text, attachments, sending, isClosed, conversationId, onSent]);

  // --- Attach handlers ----------------------------------------------------
  const addAttachment = useCallback((a) => {
    if (!a) return;
    setAttachments((prev) => (prev.length >= 5 ? prev : [...prev, a]));
  }, []);

  const onImagePicked = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axiosInstance.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res?.data?.url || res?.data?.data?.url || res?.data?.media?.url;
      if (!url) throw new Error('Upload returned no URL');
      addAttachment({ type: 'image', url, name: file.name });
    } catch (err) {
      console.error('[MerchantComposer] image upload failed:', err?.message);
      setError('Image upload failed');
    }
  }, [addAttachment]);

  const onProductPicked = useCallback((p) => {
    addAttachment({
      type: 'product_card',
      productId: p.id,
      name: p.name,
      price: p.price,
      image: p.primaryImage || (Array.isArray(p.gallery) && p.gallery[0]) || null,
      slug: p.slug || null,
    });
    setPickerOpen(null);
  }, [addAttachment]);

  const onCouponPicked = useCallback((d) => {
    addAttachment({ type: 'discount_code', code: d.code, label: formatDiscountLabel(d) });
    setPickerOpen(null);
  }, [addAttachment]);

  const onButtonSaved = useCallback(({ label, url }) => {
    addAttachment({ type: 'button_link', label, url });
    setPickerOpen(null);
  }, [addAttachment]);

  // --- @ mention trigger ---------------------------------------------------
  // Detect a trailing "@query" segment at the cursor and open the product
  // picker. On select, strip the "@query" from the text before attaching.
  const mentionMatch = useMemo(() => {
    const before = text.slice(0, textareaRef.current?.selectionStart ?? text.length);
    const m = /(^|\s)@([\w-]{0,40})$/.exec(before);
    return m ? { matchStart: before.length - m[2].length - 1, query: m[2] } : null;
  }, [text]);

  useEffect(() => {
    if (mentionMatch) setPickerOpen('mention');
    else if (pickerOpen === 'mention') setPickerOpen(null);
  }, [mentionMatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMentionPicked = useCallback((p) => {
    onProductPicked(p);
    if (mentionMatch) {
      // Strip the "@query" substring from the textarea
      setText((prev) => prev.slice(0, mentionMatch.matchStart) + prev.slice(mentionMatch.matchStart + 1 + mentionMatch.query.length));
    }
    setPickerOpen(null);
  }, [mentionMatch, onProductPicked]);

  // --- Render --------------------------------------------------------------

  if (isClosed) {
    return (
      <div className="mc-closed">
        This conversation is closed. Reopen it from the inbox to reply.
      </div>
    );
  }

  return (
    <div className="mc-wrap">
      {conversationStatus === 'AI_HANDLING' && (
        <p className="mc-hint">Replying takes over from the AI.</p>
      )}

      {attachments.length > 0 && (
        <div className="mc-attachments">
          {attachments.map((a, i) => (
            <AttachChip key={i} attachment={a} onRemove={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} />
          ))}
        </div>
      )}

      {error && <p className="mc-error">{error}</p>}

      {/* Floating picker (covers product / coupon / button / mention modes) */}
      {pickerOpen === 'product' && (
        <ProductPicker onPick={onProductPicked} onClose={() => setPickerOpen(null)} />
      )}
      {pickerOpen === 'coupon' && (
        <CouponPicker onPick={onCouponPicked} onClose={() => setPickerOpen(null)} />
      )}
      {pickerOpen === 'button' && (
        <ButtonLinkDialog onSave={onButtonSaved} onClose={() => setPickerOpen(null)} />
      )}
      {pickerOpen === 'mention' && (
        <ProductPicker onPick={onMentionPicked} onClose={() => setPickerOpen(null)} />
      )}

      <form
        className="mc-row"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        <div className="mc-actions" onMouseLeave={() => setAttachMenuOpen(false)}>
          <button
            type="button"
            className="mc-actions__trigger"
            aria-label="Attach"
            aria-expanded={attachMenuOpen}
            onClick={() => setAttachMenuOpen((v) => !v)}
          >
            <Paperclip size={16} />
          </button>
          {attachMenuOpen && (
            <div className="mc-actions__menu" role="menu">
              <button type="button" className="mc-actions__item" onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}>
                <ImageIcon size={14} /> Image
              </button>
              <button type="button" className="mc-actions__item" onClick={() => { setAttachMenuOpen(false); setPickerOpen('product'); }}>
                <Package size={14} /> Products
              </button>
              <button type="button" className="mc-actions__item" onClick={() => { setAttachMenuOpen(false); setPickerOpen('coupon'); }}>
                <Tag size={14} /> Coupon
              </button>
              <button type="button" className="mc-actions__item" onClick={() => { setAttachMenuOpen(false); setPickerOpen('button'); }}>
                <Link2 size={14} /> Button link
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImagePicked(f);
              e.target.value = '';
            }}
          />
        </div>

        <textarea
          ref={textareaRef}
          className="mc-input"
          placeholder="Type a reply…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          disabled={sending}
        />

        <button
          type="submit"
          className="mc-send"
          disabled={sending || (!text.trim() && attachments.length === 0)}
          aria-label="Send"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
};

export default MerchantComposer;
